import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Academy } from '@court-sync/shared';
import { academyService } from '../services/academy';
import { getAcademyHostContext, type AcademyHostContext } from '../lib/academyHost';

interface AcademyHostContextValue {
  host: AcademyHostContext;
  academy: Academy | null;
  /** Path slug from loaded academy (for APIs); undefined until loaded. */
  pathSlug: string | undefined;
  loading: boolean;
  error: string | null;
}

const Ctx = createContext<AcademyHostContextValue | null>(null);

export function AcademyHostProvider({
  children,
  hostContext,
}: {
  children: React.ReactNode;
  hostContext?: AcademyHostContext;
}) {
  const host = hostContext ?? getAcademyHostContext();
  const [academy, setAcademy] = useState<Academy | null>(null);
  const [loading, setLoading] = useState(host.mode === 'subdomain');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (host.mode !== 'subdomain' || !host.subdomainLabel) {
      return undefined;
    }

    let mounted = true;
    void Promise.resolve().then(() => {
      setLoading(true);
      setError(null);
    });

    academyService
      .getAcademyBySubdomain(host.subdomainLabel)
      .then((a) => {
        if (mounted) {
          setAcademy(a);
          setLoading(false);
        }
      })
      .catch((e: Error) => {
        if (mounted) {
          setAcademy(null);
          setError(e.message || 'Academy not found');
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [host.mode, host.subdomainLabel]);

  const value = useMemo(
    () => ({
      host,
      academy,
      pathSlug: academy?.slug,
      loading,
      error,
    }),
    [host, academy, loading, error]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAcademyHost(): AcademyHostContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return {
      host: getAcademyHostContext(),
      academy: null,
      pathSlug: undefined,
      loading: false,
      error: null,
    };
  }
  return ctx;
}
